<template>
  <SectionsHeroSlider
    v-if="homeData && homeData?.hero"
    :key="'hero'"
    :data="homeData?.hero"
    :firstSectionColor="homeData?.sections[0]?.backgroundColor"
  />
  <div v-if="homeData" class="w-full space-y-12 lg:space-y-20">
    <SectionsSectionRenderer
      :sections="homeData?.sections"
      :animate="animateOnScroll"
    />
  </div>
</template>

<script setup>
/* -------------------- 📦 Importlar -------------------- */
import { useApi } from "~/server/getApi";
import { useMetaTags } from "~/composables/headConfig";
import { useSiteSettingsStore } from "@/store/index";
import {
  buildJsonLdScripts,
  createFaqSchema,
  createWebPageSchema,
  createBreadcrumbSchema,
} from "~/composables/schemaOrg";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

/* -------------------- 📡 API ile Sayfa Verisini Çek -------------------- */
const {
  data: homeData,
  error: homeError,
  fetchData: fetchHomeData,
} = useApi(`home?pLevel`);
await fetchHomeData();

const route = useRoute();
const config = useRuntimeConfig();
const siteUrl = config.public.siteUrl;
const baseUrl = config.public.baseUrl;
const siteSettingsStore = useSiteSettingsStore();
const animateOnScroll = computed(() =>
  Boolean(siteSettingsStore.animateScroll),
);

/* -------------------- 🔧 SEO Etiketi -------------------- */
const seoData = computed(() => homeData.value?.seo ?? {});
const pageTitle = computed(
  () => homeData.value?.hero?.title || homeData.value?.title,
);
const pageDescription = computed(
  () => homeData.value?.hero?.description || homeData.value?.description,
);

// Call useMetaTags at the top level of script setup
useMetaTags(seoData, pageTitle, pageDescription);

/* -------------------- 📤 UseHead Scheme -------------------- */
useHead(() => {
  const homeDataScheme = homeData.value;
  if (!homeDataScheme) {
    return { script: [] };
  }

  const fullUrl = `${siteUrl}${route.fullPath}`;

  const faqSection = homeDataScheme.sections?.find(
    (section) =>
      ["sections.accordion", "sections.image-accordion"].includes(
        section.__component,
      ) && section.enabled,
  );

  const scripts = buildJsonLdScripts([
    {
      key: "schema-home-website",
      schema: createWebPageSchema({
        type: "WebSite",
        url: fullUrl,
        name: homeDataScheme?.seo?.title || homeDataScheme?.title,
        headline: homeDataScheme?.seo?.title || homeDataScheme?.title,
        description:
          homeDataScheme?.seo?.description ||
          homeDataScheme?.hero?.description ||
          homeDataScheme?.description,
        publishedAt: homeDataScheme?.publishedAt,
        updatedAt: homeDataScheme?.updatedAt,
        baseUrl,
        mainEntityType: "WebSite",
      }),
    },
    {
      key: "schema-home-faq",
      schema: createFaqSchema(faqSection?.items),
    },
    {
      key: "schema-home-breadcrumbs",
      schema: createBreadcrumbSchema(
        [
          {
            title: t("Home") || "Ana Sayfa",
            link: siteUrl,
          },
        ],
        siteUrl,
      ),
    },
  ]);

  return {
    script: scripts,
  };
});
</script>
